import type { useInteractions } from "@floating-ui/react";
import type { CSSProperties, Ref } from "react";
import { Radio, RadioGroup } from "@headlessui/react";
import { twMerge } from "tailwind-merge";
import type { SearchOption } from "./marketSearchSelections";
import type { MarketSearchGroup } from "./marketSearchGroups";
import { Icon, type IconType } from "@sellar-io/components";
import beer from "./assets/category-beer.png";
import cider from "./assets/category-cider.png";
import wine from "./assets/category-wine.png";
import spirits from "./assets/category-spirits.png";
import softs from "./assets/category-softs.png";

const SUPPLIER_LOAD_MORE_THRESHOLD_PX = 48;

const categoryImages = { beer, cider, wine, spirits, softDrinks: softs };

export const MarketSearchPanel = ({
  getFloatingProps,
  panelRef,
  style,
  listId,
  groups,
  highlight,
  optionId,
  onHighlight,
  onSelect,
  scope,
  hasSuppliers,
  onScopeChange,
  className,
  loading,
  error,
  noSupplierMatches,
  search,
  mobile,
  hasHistory,
  onClearHistory,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  getFloatingProps: ReturnType<typeof useInteractions>["getFloatingProps"];
  panelRef: Ref<HTMLDivElement>;
  style: CSSProperties;
  listId: string;
  groups: MarketSearchGroup[];
  highlight: number;
  optionId: (index: number) => string;
  onHighlight: (index: number) => void;
  onSelect: (selection: SearchOption) => void;
  scope: "market" | "suppliers";
  hasSuppliers: boolean;
  onScopeChange: (value: "market" | "suppliers") => void;
  className?: string;
  loading: boolean;
  error: boolean;
  noSupplierMatches: boolean;
  search: string;
  mobile: boolean;
  hasHistory: boolean;
  onClearHistory: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) => {
  const groupOffsets = new Map<string, number>();
  let offset = 0;
  for (const group of groups) {
    groupOffsets.set(group.id, offset);
    offset += group.options.length;
  }
  return (
    <div
      {...getFloatingProps()}
      ref={panelRef}
      style={style}
      className={twMerge(
        "z-20 flex w-[560px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-solid border-zinc-200 bg-white shadow-[2px_2px_16px_rgba(0,0,0,0.06)]",
        "pointer-events-auto",
        mobile && "w-screen max-w-none rounded-none border-x-0 shadow-none",
        className,
      )}
    >
      {hasSuppliers && (
        <RadioGroup
          aria-label="Search scope"
          value={scope}
          onChange={onScopeChange}
          className="flex shrink-0 flex-wrap gap-3 px-4 pt-4 pb-1"
        >
          {(["market", "suppliers"] as const).map((value) => {
            return (
              <Radio
                key={value}
                value={value}
                onPointerDown={(event) => event.preventDefault()}
                className="flex cursor-pointer items-center gap-1 rounded-full bg-zinc-100 px-2 py-1 text-sm leading-5 font-medium text-zinc-800 outline-offset-2 data-checked:bg-[#a34fd2] data-checked:text-white"
              >
                <Icon
                  icon={value === "market" ? "MAGNIFY" : "FORMAT_LIST_BULLETED"}
                  size="16px"
                />
                {value === "market"
                  ? "Search all of Sellar"
                  : "Search my suppliers"}
              </Radio>
            );
          })}
        </RadioGroup>
      )}
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        onScroll={(event) => {
          const el = event.currentTarget;
          if (
            hasMore &&
            !loadingMore &&
            el.scrollHeight - el.scrollTop - el.clientHeight <
              SUPPLIER_LOAD_MORE_THRESHOLD_PX
          ) {
            onLoadMore();
          }
        }}
      >
        <div role="status" className="px-5 text-sm leading-5 text-zinc-600">
          {loading && <p className="pt-4">Searching…</p>}
          {error && (
            <p className="pt-4">
              Suggestions unavailable. You can still search by keyword.
            </p>
          )}
          {noSupplierMatches && (
            <p className="pt-6 pb-2 text-base">
              No supplier results for “{search}”
            </p>
          )}
        </div>
        <div
          id={listId}
          role="listbox"
          aria-label="Market search suggestions"
          className="flex flex-col gap-4 px-2 py-4"
        >
          {groups
            .filter((group) => group.options.length)
            .map((group) => (
              <div
                key={group.id}
                role="group"
                aria-label={group.label}
                className="flex flex-col gap-2"
              >
                <div className="px-3 text-xs leading-[18px] font-semibold text-zinc-500 uppercase">
                  {group.label}
                </div>
                <div
                  className={twMerge(
                    group.grid && "grid grid-cols-2 min-[560px]:grid-cols-3",
                  )}
                >
                  {group.options.map((selection, index) => {
                    const optionIndex = groupOffsets.get(group.id)! + index;
                    return (
                      <SearchRow
                        key={optionIndex}
                        selection={selection}
                        grid={group.grid}
                        keywordSearch={group.kind === "keyword"}
                        index={optionIndex}
                        active={highlight === optionIndex}
                        optionId={optionId}
                        onHighlight={onHighlight}
                        onSelect={onSelect}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
        {hasMore && (
          <button
            type="button"
            className="p-3 text-sm text-purple-700"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading suppliers…" : "Load more suppliers"}
          </button>
        )}
      </div>
      <div
        className={twMerge(
          "flex shrink-0 flex-wrap items-center gap-3 border-t border-solid border-zinc-200 px-4 py-2 text-xs leading-[18px] font-medium text-zinc-500",
          mobile && !hasHistory && "hidden",
        )}
      >
        <div aria-hidden="true" className="hidden items-center gap-3 sm:flex">
          <span>
            <kbd className="rounded border border-zinc-200 bg-white px-0.5">
              ↑
            </kbd>{" "}
            <kbd className="rounded border border-zinc-200 bg-white px-0.5">
              ↓
            </kbd>{" "}
            to choose
          </span>
          <span>
            <kbd className="rounded border border-zinc-200 bg-white px-0.5">
              ↩
            </kbd>{" "}
            to apply
          </span>
          <span>
            <kbd className="rounded border border-zinc-200 bg-white px-0.5">
              esc
            </kbd>{" "}
            to close
          </span>
        </div>
        {hasHistory && (
          <button
            type="button"
            onPointerDown={(event) => event.preventDefault()}
            onClick={onClearHistory}
            className="ml-auto flex shrink-0 items-center justify-center gap-1 rounded-lg px-3 py-2 text-sm leading-5 font-medium text-purple-700"
          >
            <Icon icon="CLOSE" size="16px" />
            Clear recent searches
          </button>
        )}
      </div>
    </div>
  );
};

const SearchRow = ({
  selection,
  grid,
  keywordSearch,
  index,
  active,
  optionId,
  onHighlight,
  onSelect,
}: {
  selection: SearchOption;
  grid?: boolean;
  keywordSearch?: boolean;
  index: number;
  active: boolean;
  optionId: (index: number) => string;
  onHighlight: (index: number) => void;
  onSelect: (selection: SearchOption) => void;
}) => {
  const { subtitle, icon, image } = getSearchRowPresentation(
    selection,
    Boolean(keywordSearch),
  );
  return (
    <div
      role="option"
      aria-selected={active}
      id={optionId(index)}
      onPointerMove={(event) => {
        if (event.pointerType === "mouse") {
          onHighlight(index);
        }
      }}
      onPointerDown={(event) => event.preventDefault()}
      onClick={() => onSelect(selection)}
      className={twMerge(
        "flex min-h-14 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-zinc-800",
        active && "bg-purple-50 text-[#a34fd2]",
      )}
    >
      {grid && image ? (
        <div
          className={twMerge(
            "flex size-10 shrink-0 items-center justify-center rounded-lg bg-purple-50",
            active && "bg-purple-100",
          )}
        >
          <img alt="" src={image} className="max-h-8 max-w-8 object-contain" />
        </div>
      ) : (
        <div
          className={twMerge(
            "flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-purple-50",
            active && "bg-purple-100",
          )}
        >
          {(selection.kind === "supplier" || selection.kind === "product") &&
          image ? (
            <img
              alt=""
              src={image}
              className={twMerge(
                "size-10 object-contain",
                selection.kind === "product" && "object-cover",
              )}
            />
          ) : (
            <Icon icon={icon} size="24px" className="text-purple-800" />
          )}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-base leading-6 font-semibold text-zinc-800">
          {keywordSearch ? `“${selection.label}”` : selection.label}
        </div>
        {!grid && subtitle && (
          <div className="text-sm leading-5 font-normal text-zinc-500">
            {subtitle}
          </div>
        )}
      </div>
      {!grid && (
        <Icon
          icon="ARROW_RIGHT"
          size="16px"
          className={active ? "text-[#a34fd2]" : "text-zinc-400"}
        />
      )}
    </div>
  );
};

type SearchRowPresentation = {
  subtitle: string;
  icon: IconType;
  image?: string;
};
function getSearchRowPresentation(
  selection: SearchOption,
  keywordSearch: boolean,
): SearchRowPresentation {
  switch (selection.kind) {
    case "supplier":
      return {
        subtitle: selection.location || "Supplier search",
        icon: "STOREFRONT_OUTLINE",
        image: selection.logoUrl ?? undefined,
      };
    case "style":
      return {
        subtitle: "Drinks style",
        icon: selection.categoryKey === "wine" ? "BOTTLE" : "CAN",
      };
    case "category":
      return {
        subtitle: "Drinks category",
        icon: "MAGNIFY",
        image:
          selection.categoryKey === "mead"
            ? undefined
            : categoryImages[selection.categoryKey],
      };
    case "text":
      return {
        subtitle: keywordSearch
          ? `Search matches for “${selection.value}”`
          : "Keyword search",
        icon: "MAGNIFY",
      };
    case "filters":
      return {
        subtitle: "Filters applied",
        icon: selection.preset === "imperialStoutCans" ? "CAN" : "KEG",
      };
    case "filterQuery":
      return { subtitle: "Filters applied", icon: "FILTER_VARIANT" };
    case "product":
      return {
        subtitle: selection.supplierName,
        icon: "MAGNIFY",
        image: selection.imageUrl ?? undefined,
      };
    case "container":
      return {
        subtitle: "Container type",
        icon: selection.value.toLowerCase().includes("keg")
          ? "KEG"
          : selection.value === "can"
            ? "CAN"
            : "BOTTLE",
      };
    case "dietary":
      return { subtitle: "Dietary filter", icon: "MAGNIFY" };
  }
}
